/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Col,
  CustomInput,
  FormGroup,
  Input,
  Label,
  Row,
  Button,
} from 'reactstrap';
import api from '~/services/api';
import { toast } from 'react-toastify';
import {
  APIProvider,
  AdvancedMarker,
  Map,
  Pin,
  useMap,
  useMapsLibrary,
} from '@vis.gl/react-google-maps';
import { stateList } from '~/util/stateList';
import { getCitiesByUf } from '~/util/ibge';
import {
  extractLatLng,
  formatCEP,
  formatResolvedAddressLabel,
  normalizeLatLng,
  onlyDigits,
  parseAddressComponents,
  pickGeocodeResults,
} from '~/util/googleAddress';

const DEFAULT_CENTER = { lat: -23.55052, lng: -46.633308 };

// O CEP fica fora da string e entra como componentRestriction, mesmo
// critério do StoreLocationPicker.
const buildAddressString = candidate =>
  [
    candidate.street_name,
    candidate.number && candidate.number !== 'sn' ? candidate.number : '',
    candidate.neighborhood,
    candidate.city,
    candidate.state,
  ]
    .filter(Boolean)
    .join(', ');

// O <Map> usa defaultCenter (não-controlado), então centraliza apenas no
// mount. Este bridge devolve a instância para podermos dar panTo quando
// o pin muda por CEP, busca ou reverse geocode.
const MapInstanceBridge = ({ onMapReady }) => {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    onMapReady(map);
  }, [map, onMapReady]);

  return null;
};

const DeliveryAddressPickerContent = ({
  initialAddress,
  initialLocation,
  onChange,
  mapHeight = 260,
}) => {
  const placesLibrary = useMapsLibrary('places');
  const geocodingLibrary = useMapsLibrary('geocoding');

  const searchInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const [geocoder, setGeocoder] = useState(null);
  const [address, setAddress] = useState(initialAddress || {});
  const [markerPosition, setMarkerPosition] = useState(
    normalizeLatLng(initialLocation)
  );
  const [cityList, setCityList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);

  const [searchValue, setSearchValue] = useState('');
  const lastCepLookupRef = useRef('');

  useEffect(() => {
    if (geocodingLibrary) setGeocoder(new geocodingLibrary.Geocoder());
  }, [geocodingLibrary]);

  // Mostra o endereço vigente no campo de busca ao abrir.
  useEffect(() => {
    if (!initialAddress) return;

    setSearchValue(formatResolvedAddressLabel(initialAddress));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cidades por UF (util centralizado que criamos)
  useEffect(() => {
    const loadCities = async () =>
      setCityList(await getCitiesByUf(address.state));
    loadCities();
  }, [address.state]);

  // Refs espelham o state para o listener do Autocomplete (criado uma
  // única vez) sempre enxergar os valores atuais.
  const addressRef = useRef(address);
  const locationRef = useRef(markerPosition);

  useEffect(() => {
    addressRef.current = address;
  }, [address]);

  useEffect(() => {
    locationRef.current = markerPosition;
  }, [markerPosition]);

  // Centraliza o mapa sempre que o pin muda de lugar.
  useEffect(() => {
    if (!mapInstance || !markerPosition?.lat || !markerPosition?.lng) return;

    mapInstance.panTo(markerPosition);
    mapInstance.setZoom(17);
  }, [mapInstance, markerPosition]);

  // Toda mudança sobe consolidada para o pai
  // nextLocation undefined = mantém o pin atual (edição de campo);
  // null = limpa (endereço novo sem coordenada confiável, o backend
  // geocodifica); objeto = novo pin.
  const emit = useCallback(
    (nextAddress, nextLocation) => {
      setAddress(nextAddress);
      addressRef.current = nextAddress;

      if (nextLocation !== undefined) {
        const normalized = normalizeLatLng(nextLocation);

        setMarkerPosition(normalized);
        locationRef.current = normalized;
      }

      onChange({
        address: nextAddress,
        location: locationRef.current,
      });
    },
    [onChange]
  );

  const setField = (field, value) =>
    emit({ ...addressRef.current, [field]: value });

  // Pin arrastado / clique no mapa => reverse geocode preenche o form
  const reverseGeocode = useCallback(
    async position => {
      if (!geocoder || !position) return;
      setLoading(true);
      try {
        const { results } = await geocoder.geocode({
          location: position,
          language: 'pt-BR',
          region: 'br',
        });
        const result = results?.[0];
        if (!result) {
          toast.error('Não foi possível identificar o endereço desse ponto.');
          return;
        }
        const resolved = parseAddressComponents(
          result.address_components || [],
          result.formatted_address || ''
        );
        emit({ ...addressRef.current, ...resolved }, position);
      } catch (e) {
        toast.error('Não foi possível atualizar o endereço pelo mapa.');
      } finally {
        setLoading(false);
      }
    },
    [geocoder, emit]
  );

  // Autocomplete do Places no campo de busca
  useEffect(() => {
    if (!placesLibrary || !searchInputRef.current || autocompleteRef.current)
      return undefined;

    const autocomplete = new placesLibrary.Autocomplete(
      searchInputRef.current,
      {
        fields: ['address_components', 'formatted_address', 'geometry'],
        componentRestrictions: { country: ['br'] },
        types: ['address'],
      }
    );
    autocompleteRef.current = autocomplete;

    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place?.geometry?.location) return;
      const resolved = parseAddressComponents(
        place.address_components || [],
        place.formatted_address || ''
      );
      const position = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
      emit({ ...addressRef.current, ...resolved }, position);
      setSearchValue(place.formatted_address || '');
    });

    return () => {
      listener?.remove();
      autocompleteRef.current = null;
    };
  }, [placesLibrary, emit]);

  // ZERO_RESULTS chega como exceção, não como lista vazia.
  const geocodeSafely = useCallback(
    async request => {
      try {
        return await geocoder.geocode(request);
      } catch (error) {
        const message = String(error?.message || error || '');

        if (
          error?.code === 'ZERO_RESULTS' ||
          message.includes('ZERO_RESULTS')
        ) {
          return null;
        }

        throw error;
      }
    },
    [geocoder]
  );

  // Três tentativas, afrouxando a restrição a cada rodada: endereço +
  // CEP, endereço + país, só CEP. Endereços inconsistentes (rua de uma
  // cidade com o município de outra) só resolvem a partir da segunda.
  const geocodeAddress = useCallback(
    async candidate => {
      if (!geocoder) return null;

      const addressString = buildAddressString(candidate);
      const cepDigits = onlyDigits(candidate.cep);

      if (!addressString && !cepDigits) return null;

      const strictResponse = addressString
        ? await geocodeSafely({
            address: addressString,
            componentRestrictions: cepDigits
              ? { country: 'BR', postalCode: cepDigits }
              : { country: 'BR' },
            language: 'pt-BR',
            region: 'br',
          })
        : null;

      const countryOnlyResponse =
        !strictResponse?.results?.length && addressString
          ? await geocodeSafely({
              address: addressString,
              componentRestrictions: { country: 'BR' },
              language: 'pt-BR',
              region: 'br',
            })
          : null;

      const cepOnlyResponse =
        !strictResponse?.results?.length &&
        !countryOnlyResponse?.results?.length &&
        cepDigits
          ? await geocodeSafely({
              address: formatCEP(cepDigits),
              componentRestrictions: { country: 'BR' },
              language: 'pt-BR',
              region: 'br',
            })
          : null;

      const results = pickGeocodeResults(
        strictResponse,
        countryOnlyResponse,
        cepOnlyResponse
      );

      if (!results.length) return null;

      const [first] = results;

      return {
        lat: first.geometry.location.lat(),
        lng: first.geometry.location.lng(),
      };
    },
    [geocodeSafely, geocoder]
  );

  // Recentraliza o pin a partir dos campos preenchidos manualmente.
  const recenterFromFields = useCallback(async () => {
    setLoading(true);

    try {
      const position = await geocodeAddress(addressRef.current);

      if (!position) {
        toast.warning(
          'Não foi possível posicionar este endereço no mapa. Arraste o pin para o ponto exato.'
        );
      }

      emit(addressRef.current, position);
    } catch (error) {
      toast.error('Não foi possível recentralizar o mapa pelo endereço.');
    } finally {
      setLoading(false);
    }
  }, [emit, geocodeAddress]);

  const lookupCep = useCallback(
    async rawValue => {
      const cepDigits = onlyDigits(rawValue);

      if (cepDigits.length !== 8) return;
      if (lastCepLookupRef.current === cepDigits) return;

      lastCepLookupRef.current = cepDigits;
      setLoading(true);

      try {
        const { data } = await api.get(`cep/${formatCEP(cepDigits)}`);

        const resolved = {
          ...addressRef.current,
          cep: data.cep || formatCEP(cepDigits),
          street_name: data.street_name || '',
          neighborhood: data.neighborhood || '',
          city: data.city || '',
          state: data.state || '',
        };

        const position = await geocodeAddress(resolved);

        if (!position) {
          toast.warning(
            'Não foi possível posicionar este endereço no mapa. Arraste o pin para o ponto exato.'
          );
        }

        emit(resolved, position);
        setSearchValue(formatResolvedAddressLabel(resolved));
      } catch (error) {
        toast.error('Não foi possível localizar esse CEP.');
      } finally {
        setLoading(false);
      }
    },
    [emit, geocodeAddress]
  );

  const handleSearchChange = useCallback(
    event => {
      const nextValue = event.target.value;
      setSearchValue(nextValue);

      const cepDigits = onlyDigits(nextValue);

      if (cepDigits.length === 8 && /^[0-9\s.-]+$/.test(nextValue)) {
        lookupCep(nextValue);
      } else {
        lastCepLookupRef.current = '';
      }
    },
    [lookupCep]
  );

  return (
    <div className="border rounded p-3">
      <div className="mb-3">
        <Label>CEP ou Endereço</Label>
        <input
          ref={searchInputRef}
          type="text"
          className="form-control"
          placeholder="Digite o CEP ou pesquise o endereço de destino"
          value={searchValue}
          onChange={handleSearchChange}
        />
      </div>

      <div className="d-flex align-items-start justify-content-between mb-2">
        <div className="text-muted font-size-12">
          Arraste o pin para o local exato onde o entregador deve chegar.
        </div>
        <Button
          type="button"
          color="primary"
          className="new-layout-btn new-layout-btn-transparent"
          onClick={recenterFromFields}
          disabled={loading}
        >
          Recentralizar pelo endereço
        </Button>
      </div>

      <div
        style={{
          height: mapHeight,
          borderRadius: 8,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {loading && (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              background: 'rgba(255,255,255,0.65)',
            }}
          >
            Atualizando...
          </div>
        )}
        <Map
          id="delivery-address-picker-map"
          mapId={process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID'}
          defaultCenter={markerPosition || DEFAULT_CENTER}
          defaultZoom={markerPosition ? 16 : 5}
          gestureHandling="greedy"
          style={{ width: '100%', height: '100%' }}
          onClick={e => reverseGeocode(extractLatLng(e))}
        >
          <MapInstanceBridge onMapReady={setMapInstance} />

          {markerPosition && (
            <AdvancedMarker
              position={markerPosition}
              draggable
              onDragEnd={e => reverseGeocode(extractLatLng(e))}
            >
              <Pin />
            </AdvancedMarker>
          )}
        </Map>
      </div>

      <Row className="w-100 mx-0 mt-3">
        <Col lg={8} className="px-0 pr-lg-2">
          <FormGroup>
            <Label>Logradouro</Label>
            <Input
              value={address.street_name || ''}
              onChange={e => setField('street_name', e.target.value)}
            />
          </FormGroup>
        </Col>
        <Col lg={4} className="px-0 pl-lg-2">
          <Label>Número</Label>
          <CustomInput
            type="switch"
            id="deliveryNoNumberSwitch"
            name="deliveryNoNumber"
            label="Sem Número"
            checked={address.number === 'sn'}
            className="text-primary float-right"
            onChange={() =>
              setField('number', address.number === 'sn' ? '' : 'sn')
            }
          />
          <FormGroup>
            <Input
              value={address.number === 'sn' ? '' : address.number || ''}
              disabled={address.number === 'sn'}
              onChange={e => setField('number', e.target.value)}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row className="w-100 mx-0">
        <Col lg={4} className="px-0 pr-lg-2">
          <FormGroup>
            <Label>Bairro</Label>
            <Input
              value={address.neighborhood || ''}
              onChange={e => setField('neighborhood', e.target.value)}
            />
          </FormGroup>
        </Col>
        <Col lg={4} className="px-0 px-lg-2">
          <FormGroup>
            <Label>Estado</Label>
            <Input
              type="select"
              value={address.state || ''}
              onChange={e => setField('state', e.target.value)}
            >
              <option value="">UF</option>
              {stateList.map(s => (
                <option key={s.value} value={s.value}>
                  {s.value}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col lg={4} className="px-0 pl-lg-2">
          <FormGroup>
            <Label>Cidade</Label>
            <Input
              type="select"
              value={address.city || ''}
              disabled={!address.state}
              onChange={e => setField('city', e.target.value)}
            >
              <option value="">Selecione</option>
              {cityList.map(c => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
      </Row>
      <Row className="w-100 mx-0">
        <Col lg={4} className="px-0 pr-lg-2">
          <FormGroup>
            <Label>CEP</Label>
            <Input
              value={address.cep || ''}
              onChange={e => setField('cep', e.target.value)}
            />
          </FormGroup>
        </Col>
        <Col lg={8} className="px-0 pl-lg-2">
          <FormGroup>
            <Label>Complemento</Label>
            <Input
              value={address.complement || ''}
              onChange={e => setField('complement', e.target.value)}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row className="w-100 mx-0">
        <Col className="px-0">
          <FormGroup>
            <Label>Informações Adicionais</Label>
            <Input
              value={address.additional_information || ''}
              onChange={e => setField('additional_information', e.target.value)}
              placeholder="Ponto de referência, instruções para o entregador"
            />
          </FormGroup>
        </Col>
      </Row>
    </div>
  );
};

const DeliveryAddressPicker = props => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
  if (!apiKey) return null;

  return (
    <APIProvider
      apiKey={apiKey}
      language="pt-BR"
      region="BR"
      libraries={['places']}
    >
      <DeliveryAddressPickerContent {...props} />
    </APIProvider>
  );
};

export default DeliveryAddressPicker;
